import React, { useEffect, useState } from 'react';
import './styles/PaymentPlanSchedules.scss';
import { duckify, filterDuck } from 'utils/duckify';
import { Button } from 'react-bootstrap';
import {
  billingContacts,
  contactInstallmentSchedules,
  contactInstallmentSchedulesForm, billingItems, billingPayers as billingPayersDuck, modals
} from 'ducks';
import { Form, actions as formActions, Control } from 'react-redux-form';
import PropTypes from 'prop-types';
import ModalComponent from '../../ModalComponent';
import ScheduleEditor from './ScheduleEditor';
import registeredComponent from '../../../utils/registeredComponent';
import ValidationErrors from '../../forms/ValidationErrors';
import Placeholder from '../../Placeholder';
import { formatBillingContact } from '../../billing/BillingItemList';
import { stringParams } from '../../../utils';
import ControlGroup from '../../forms/ControlGroup';
import UnorderedList from '../../shared/UnorderedList';
import { BillingContactType } from '../../../propTypes';

// Allows editing installment arrangement after contract submission
const InstallmentEditor = (
  {
    contacts,
    model,
    payerContactId,
    allDucksReady,
    duckMeta,
    dispatch,
    loadPayers,
    loadSchedule,
    loadScheduleIntoForm,
    saveForm,
    markItemsDirty,
    showError,
    hideInstallmentEditor
  }
) => {
  const [billingContact, setBillingContact] = useState(null);
  const [contactInstallments, setInstallments] = useState(null);
  const [payers, setPayers] = useState([]);
  const [saving, setSaving] = useState(false);

  const updateBillingContact = (bc) => {
    setBillingContact(bc);
    setPayers([]);

    loadSchedule(bc).then((loadedSchedule) => {
      loadScheduleIntoForm(loadedSchedule);
      setInstallments(loadedSchedule); // Do this second so loaded does not become true too soon
    });

    loadPayers(bc.contact)
      .then((billingPayers) => setPayers(billingPayers.filter(payer => payer.payer_contact.id !== payerContactId)));
  };

  useEffect(() => {
    if (duckMeta.contacts.ready && contacts.length > 0) {
      // Default to first bc
      updateBillingContact(contacts[0]);
    }
  }, [duckMeta.contacts.ready]);

  const save = () => {
    setSaving(true);
    saveForm(model)
      .then(() => hideInstallmentEditor())
      .then(() => markItemsDirty())
      .catch((response) => {
        if (!response.contact_installment_schedule) {
          hideInstallmentEditor();
          showError(response.error);
          markItemsDirty();
        } else {
          loadScheduleIntoForm(response.contact_installment_schedule);
          setSaving(false);
        }
      });
  };

  const loaded = allDucksReady
    && ((contacts && contacts.length === 0)
      || ((contactInstallments !== null) && (contactInstallments.id === billingContact.id)));
  const hasPayments = loaded && model.number_of_payments > 0;

  const buttons = [
    <Button
      variant='primary'
      disabled={saving || !hasPayments}
      onClick={save}
    >
      {saving ? 'Saving...' : 'Save'}
    </Button>
  ];

  return (
    <ModalComponent buttons={buttons} title='Rearrange Installments'>
      {allDucksReady ? (
        <Form model={contactInstallmentSchedulesForm.model}>
          { hasPayments && (
            <div className='installment-editor-warning'>
              {payers.length > 0 && (
                <UnorderedList
                  message='Changing the schedule will also impact the following payers:'
                  className='warning'
                  extraClassMessage='smaller'
                  actions={payers.map(payer => payer.payer_contact.name)}
                />
              )}
              <ValidationErrors errors={model.errors} />
            </div>
          )}
          { loaded && !hasPayments && (
            <div className='installment-editor-warning'>
              <UnorderedList
                message='There are no installments for this contact.'
                className='warning'
                extraClassMessage='smaller'
                actions={[]}
              />
            </div>
          )}
          <ControlGroup label='Billing Contact'>
            <Control.select
              model='.billing_contact_id'
              defaultValue={billingContact ? billingContact.id : ''}
              className='billing-contact form-select form-select-sm'
              onChange={(e) => updateBillingContact(contacts.find(bc => bc.id === e.target.value))}
            >
              {contacts.map(c => <option value={c.id} key={c.id}>{formatBillingContact(c)}</option>)}
            </Control.select>
          </ControlGroup>
          { loaded ? (
            <div className='form-horizontal'>
              {hasPayments && (
                <ScheduleEditor
                  allDucksReady={allDucksReady}
                  formDuck={contactInstallmentSchedulesForm}
                  formNumOfPayments={model.number_of_payments}
                  formScheduleItems={model.schedule_items}
                  databaseScheduleItems={contactInstallments.schedule_items}
                  schoolYear={billingContact.school_year}
                  dispatch={dispatch}
                  defaultNumPayments={1}
                  numPastInstallments={allDucksReady ? contactInstallments.num_past_installments : 0}
                  itemsName='schedule_items'
                />
              )}
            </div>
          ) : <Placeholder.loading /> }
        </Form>
      ) : <Placeholder.loading />
      }
    </ModalComponent>
  );
};
InstallmentEditor.propTypes = {
  contacts: PropTypes.arrayOf(BillingContactType).isRequired,
  model: PropTypes.shape({
    number_of_payments: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    schedule_items: PropTypes.arrayOf(PropTypes.shape({
      year: PropTypes.number,
      month: PropTypes.number,
      percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })),
    errors: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  payerContactId: PropTypes.string.isRequired,
  allDucksReady: PropTypes.bool.isRequired,
  duckMeta: PropTypes.shape({
    contacts: PropTypes.shape({
      ready: PropTypes.bool
    })
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  loadPayers: PropTypes.func.isRequired,
  loadSchedule: PropTypes.func.isRequired,
  loadScheduleIntoForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  markItemsDirty: PropTypes.func.isRequired,
  showError: PropTypes.func.isRequired,
  hideInstallmentEditor: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  ...state.contactInstallmentSchedulesForm
});

const mapDucksToProps = ({ payerContactId }) => ({
  // Only will load the one billing payer since we filter by it, otherwise would include_billing_payers
  contacts: filterDuck(billingContacts, { payer_contact_id: payerContactId })
});

const mapDispatchToProps = (dispatch) => ({
  loadPayers: (contact) => {
    const payersKey = stringParams({ contact_id: contact.id });
    return dispatch(billingPayersDuck.actions.load({ contact_id: contact.id }, payersKey))
      .then(billingPayersDuck.actions.denormalizeObjects);
  },
  loadSchedule: (billingContact) => dispatch(contactInstallmentSchedules.actions.get(billingContact.id))
    .then(contactInstallmentSchedules.actions.denormalizeObjects),
  loadScheduleIntoForm: (schedule) => dispatch(formActions.load('contactInstallmentSchedulesForm.model', schedule)),
  saveForm: (model) => dispatch(contactInstallmentSchedulesForm.actions
    .save({ ...model })),
  markItemsDirty: () => dispatch(billingItems.actions.markDirty()),
  showError: (error) => dispatch(modals.actions.show('UnexpectedError', { message: error })),
  hideInstallmentEditor: () => dispatch(modals.actions.hide('InstallmentEditor'))
});

export default registeredComponent('InstallmentEditor')(
  duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(InstallmentEditor)
);
